import { useState, type ChangeEvent, type FormEvent } from 'react';
import { tripsApi, costsApi, shouldQueueOffline, type Trip } from '@/api/client';
import { useAsync } from '@/hooks';
import { CrewLayout } from '@/components/Layout';
import { Nav } from '@/components/Nav';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { OfflineIndicator } from '@/offline';
import { queueEvent, cacheCrewTrip, getCachedCrewTrip } from '@/offline/queue';
import { newCrewActionMeta } from '@/lib/offlineReplay';
import { formatPeso, humanize } from '@/lib/format';
import { compressImage } from '@/lib/imageCompression';
import { crewNavItems } from '@/pages/navItems';
import { CrewActionBar, CrewIcon, CrewMessage } from '@/components/CrewUi';

const COST_TYPES = ['tolls', 'fuel', 'parking', 'meals', 'other'];
const COST_TYPE_LABELS: Record<string, string> = {
  tolls: 'Tolls',
  fuel: 'Fuel',
  parking: 'Parking',
  meals: 'Meals',
  other: 'Other',
};

const EMPTY_FORM = {
  type: 'tolls',
  amount: '',
  description: '',
  receiptKey: '',
  litres: '',
  odometer: '',
  vendor: '',
};

export function CrewCosts() {
  // Same cached-trip fallback as Scan/Sign so costs can be logged with no signal.
  const { data: trip, loading: tripLoading, error: tripError, reload: reloadTrip } = useAsync(async () => {
    try {
      const current = await tripsApi.myCurrent();
      await cacheCrewTrip(current as unknown as Record<string, unknown> | null);
      return current;
    } catch (err) {
      const cached = await getCachedCrewTrip<Trip>();
      if (cached) return cached;
      throw err;
    }
  }, []);
  const { data: costDetail, reload } = useAsync(
    () => (trip ? costsApi.list(trip.id) : Promise.resolve(null)),
    [trip?.id],
  );
  const costs = costDetail?.direct ?? [];

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');

  const isFuel = form.type === 'fuel';

  const handlePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress before upload — mobile photos are often several MB; capping the
    // longest edge at 1600px and re-encoding at JPEG q0.7 keeps this small over cellular.
    setPhotoLoading(true);
    setFormError('');
    try {
      const compressed = await compressImage(file);
      setForm((f) => ({ ...f, receiptKey: compressed }));
    } catch {
      setForm((f) => ({ ...f, receiptKey: '' }));
      setFormError('Could not read the receipt photo — try taking it again.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trip || !form.amount || photoLoading) return;
    setFormError('');

    if (!form.receiptKey) {
      setFormError('A receipt photo is required to log a cost.');
      return;
    }
    if (isFuel && (!form.litres || !form.odometer)) {
      setFormError('Litres and odometer reading are required for fuel costs.');
      return;
    }

    setLoading(true);
    setMessage('');
    const payload = {
      type: form.type,
      amount: form.amount,
      description: form.description || undefined,
      receiptKey: form.receiptKey,
      litres: isFuel ? form.litres : undefined,
      odometer: isFuel ? Number(form.odometer) : undefined,
      vendor: form.vendor || undefined,
    };
    // Same key/time for the online attempt and any offline replay of it.
    const action = newCrewActionMeta();
    try {
      await costsApi.create(trip.id, payload, action);
      setForm(EMPTY_FORM);
      setMessage(`${COST_TYPE_LABELS[payload.type] ?? humanize(payload.type)} ${formatPeso(payload.amount)} logged`);
      reload();
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          await queueEvent({
            type: 'cost',
            payload: { tripId: trip.id, ...payload },
            ...action,
          });
          setMessage('Saved on phone – will send when signal returns');
          setForm(EMPTY_FORM);
        } catch (queueErr) {
          setFormError((queueErr as Error).message ?? 'Could not save cost offline');
        }
      } else {
        const apiErr = err as { message?: string };
        setFormError(apiErr.message ?? 'Failed to log cost');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CrewLayout>
      <OfflineIndicator />
      <div className="crew-content">
        <Nav items={crewNavItems} variant="crew" />

        <div className="costs-page">
          <h2 className="crew-title">Cost log</h2>

          {tripLoading && !trip ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : tripError && !trip ? (
            <div className="crew-empty-card">
              <p>Can&apos;t reach the server. Check your signal.</p>
              <CrewActionBar>
                <Button type="button" variant="crew" size="crew" fullWidth onClick={() => void reloadTrip()}>
                  Try again
                </Button>
              </CrewActionBar>
            </div>
          ) : !trip ? (
            <p className="crew-muted">No active trip. Costs can be logged when you have an assigned trip.</p>
          ) : (
            <>
              <p className="crew-subtitle">Trip: {trip.reference}</p>

              <form onSubmit={handleSubmit} className="costs-form">
                <div className="form-group">
                  <span className="crew-field-label" id="cost-type-label">Type</span>
                  <div className="cost-type-chips" role="radiogroup" aria-labelledby="cost-type-label">
                    {COST_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="radio"
                        aria-checked={form.type === t}
                        className={`cost-type-chip ${form.type === t ? 'active' : ''}`}
                        onClick={() => setForm({ ...form, type: t })}
                      >
                        {COST_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </div>

                <Input
                  label="Amount (₱)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  placeholder="0.00"
                />

                {isFuel && (
                  <>
                    <Input
                      label="Litres"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={form.litres}
                      onChange={(e) => setForm({ ...form, litres: e.target.value })}
                      required
                      placeholder="0.00"
                    />
                    <Input
                      label="Odometer reading (km)"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={form.odometer}
                      onChange={(e) => setForm({ ...form, odometer: e.target.value })}
                      required
                      placeholder="e.g. 45210"
                    />
                    <Input
                      label="Vendor / station"
                      value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      placeholder="Shell NLEX"
                    />
                  </>
                )}

                <Input
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="NLEX toll, SCTEX…"
                />

                <div className="form-group">
                  <span className="crew-field-label">Receipt photo (required)</span>
                  <label className={`crew-photo-btn ${photoLoading || loading ? 'is-disabled' : ''}`}>
                    <CrewIcon name="camera" />
                    {form.receiptKey ? 'Retake receipt photo' : 'Take receipt photo'}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      aria-label="Receipt photo (required)"
                      onChange={(e) => void handlePhotoChange(e)}
                      disabled={photoLoading || loading}
                    />
                  </label>
                  {photoLoading && <span className="crew-photo-status waiting">Preparing photo…</span>}
                  {!photoLoading && form.receiptKey && <span className="crew-photo-status">Receipt attached</span>}
                </div>

                {formError && <CrewMessage kind="error">{formError}</CrewMessage>}
                {message && <CrewMessage kind={message.startsWith('Saved on phone') ? 'info' : 'ok'}>{message}</CrewMessage>}

                <CrewActionBar>
                  <Button
                    type="submit"
                    variant="crew"
                    size="crew"
                    fullWidth
                    loading={loading}
                    disabled={loading || photoLoading}
                  >
                    Log cost
                  </Button>
                </CrewActionBar>
              </form>

              {costs.length > 0 && (
                <div className="costs-list">
                  <span className="crew-section-label">Logged costs</span>
                  {costs.map((c) => (
                    <div key={c.id} className="crew-card cost-item">
                      <span className="cost-item-type">
                        {COST_TYPE_LABELS[c.category] ?? humanize(c.category)}
                        {c.category === 'fuel' && c.quantity ? ` · ${c.quantity} L` : ''}
                      </span>
                      <span className="cost-item-amount crew-num">{formatPeso(c.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        .cost-type-chips { display: flex; flex-wrap: wrap; gap: var(--cs-2); }
        .cost-type-chip {
          min-height: var(--crew-touch);
          min-width: 72px;
          padding: 0 var(--cs-4);
          border-radius: var(--crew-r);
          border: 2px solid var(--crew-outline);
          background: var(--crew-raised);
          color: var(--crew-white);
          font-size: var(--crew-fs-body);
          font-weight: 600;
          cursor: pointer;
        }
        .cost-type-chip.active { background: var(--crew-signal); color: var(--crew-bg); border-color: var(--crew-signal); font-weight: 700; }
        .costs-form .form-group { margin-bottom: var(--crew-gap); }
        .cost-item { display: flex; align-items: center; justify-content: space-between; gap: var(--cs-3); }
        .cost-item-type { min-width: 0; overflow-wrap: anywhere; }
        .cost-item-amount { font-weight: 700; font-size: var(--crew-fs-card); white-space: nowrap; }
      `}</style>
    </CrewLayout>
  );
}
